/* global process */
import { useCallback, useEffect, useMemo, useRef } from 'react';

import { useTheme } from '@/app/ThemeContext';
import {
  createAttributeIconDataURL,
  createComputerIconDataURL,
  createRiskIconDataURL,
  createSeedIconDataURL,
  createTagIconDataURL,
  createTechnologyIconDataURL,
  createWebpageIconDataURL,
} from '@/components/icons/nodeIcons';
import { RiskSeverity } from '@/types';

/**
 * LRUCache - Least Recently Used cache implementation
 *
 * Automatically evicts least recently used items when capacity is reached.
 * Uses a Map for O(1) access and maintains access order.
 */
class LRUCache<K, V> {
  private cache: Map<K, V>;
  private readonly maxSize: number;

  constructor(maxSize: number = 500) {
    this.cache = new Map();
    this.maxSize = maxSize;
  }

  get(key: K): V | undefined {
    const value = this.cache.get(key);
    if (value !== undefined) {
      // Move to end (most recently used)
      this.cache.delete(key);
      this.cache.set(key, value);
    }
    return value;
  }

  set(key: K, value: V): void {
    // Remove if already exists to update position
    if (this.cache.has(key)) {
      this.cache.delete(key);
    }
    // Check capacity and evict oldest if needed
    else if (this.cache.size >= this.maxSize) {
      // First key is the oldest (least recently used)
      const oldestKey = this.cache.keys().next().value;
      if (oldestKey !== undefined) {
        this.cache.delete(oldestKey);
      }
    }
    this.cache.set(key, value);
  }

  has(key: K): boolean {
    return this.cache.has(key);
  }

  get size(): number {
    return this.cache.size;
  }

  clear(): void {
    this.cache.clear();
  }
}

export interface ImageCache {
  getNodeImage: (
    key: string,
    isHighlighted: boolean,
    borderColorOverride?: string
  ) => string;
  getAttributeImage: (key: string, isHighlighted: boolean) => string;
  getSeedImage: (
    key: string,
    isHighlighted: boolean,
    borderColorOverride?: string
  ) => string;
  getRiskImage: (
    key: string,
    isHighlighted: boolean,
    severity: RiskSeverity
  ) => string;
  getTechnologyImage: (key: string, isHighlighted?: boolean) => string;
  getTagImage: (key: string, isHighlighted?: boolean) => string;
  getWebpageImage: (key: string, isHighlighted?: boolean) => string;
}

/**
 * useImageCache - Node Icon Caching Hook with LRU Eviction
 *
 * This custom hook provides efficient caching for dynamically generated node icons
 * in the NodeGraph visualization. It manages separate LRU caches for different node
 * types to optimize memory usage and rendering performance.
 *
 * Performance Optimizations:
 * - LRU cache with 500 image limit per type
 * - Automatic eviction of least recently used images
 * - Development mode cache statistics
 * - Memory-efficient data URL storage
 */
export const useImageCache = (): ImageCache => {
  const { currentTheme } = useTheme();

  // LRU caches for different node types (500 images per type)
  const nodeImageCacheRef = useRef<LRUCache<string, string>>(new LRUCache(500));
  const attributeImageCacheRef = useRef<LRUCache<string, string>>(
    new LRUCache(500)
  );
  const seedImageCacheRef = useRef<LRUCache<string, string>>(new LRUCache(500));
  const riskImageCacheRef = useRef<LRUCache<string, string>>(new LRUCache(500));
  const technologyImageCacheRef = useRef<LRUCache<string, string>>(
    new LRUCache(500)
  );
  const tagImageCacheRef = useRef<LRUCache<string, string>>(new LRUCache(500));
  const webpageImageCacheRef = useRef<LRUCache<string, string>>(
    new LRUCache(500)
  );

  // Track cache statistics in development mode
  const statsRef = useRef({
    hits: 0,
    misses: 0,
  });

  // Log cache statistics periodically in development
  useEffect(() => {
    if (process.env.NODE_ENV === 'development') {
      const interval = setInterval(() => {
        const totalRequests = statsRef.current.hits + statsRef.current.misses;
        if (totalRequests > 0) {
        }
      }, 30000);

      return () => clearInterval(interval);
    }
  }, []);

  // Node image getter with LRU cache and statistics tracking
  const getNodeImage = useCallback(
    (
      key: string,
      isHighlighted: boolean = false,
      borderColorOverride?: string
    ): string => {
      const cacheKey = `${key}-${isHighlighted}-${borderColorOverride || 'default'}`;
      const cache = nodeImageCacheRef.current;
      const cached = cache.get(cacheKey);

      if (cached !== undefined) {
        if (process.env.NODE_ENV === 'development') {
          statsRef.current.hits++;
        }
        return cached;
      }

      if (process.env.NODE_ENV === 'development') {
        statsRef.current.misses++;
      }

      const isDomainController = !!borderColorOverride;
      const image = createComputerIconDataURL(
        currentTheme,
        isHighlighted || isDomainController
      );
      cache.set(cacheKey, image);
      return image;
    },
    [currentTheme]
  );

  // Attribute image getter with LRU cache
  const getAttributeImage = useCallback(
    (key: string, isHighlighted: boolean = false): string => {
      const cacheKey = `${key}-${isHighlighted}`;
      const cache = attributeImageCacheRef.current;
      const cached = cache.get(cacheKey);

      if (cached !== undefined) {
        return cached;
      }

      const image = createAttributeIconDataURL(currentTheme, isHighlighted);
      cache.set(cacheKey, image);
      return image;
    },
    [currentTheme]
  );

  // Seed image getter with LRU cache
  const getSeedImage = useCallback(
    (key: string, isHighlighted: boolean = false): string => {
      const cacheKey = `${key}-${isHighlighted}`;
      const cache = seedImageCacheRef.current;
      const cached = cache.get(cacheKey);

      if (cached !== undefined) {
        return cached;
      }

      const image = createSeedIconDataURL(currentTheme, isHighlighted);
      cache.set(cacheKey, image);
      return image;
    },
    [currentTheme]
  );

  // Risk image getter with LRU cache
  const getRiskImage = useCallback(
    (
      key: string,
      isHighlighted: boolean = false,
      severity: RiskSeverity
    ): string => {
      const cacheKey = `${key}-${isHighlighted}-${severity}`;
      const cache = riskImageCacheRef.current;
      const cached = cache.get(cacheKey);

      if (cached !== undefined) {
        return cached;
      }

      const image = createRiskIconDataURL(
        currentTheme,
        severity,
        isHighlighted
      );
      cache.set(cacheKey, image);
      return image;
    },
    [currentTheme]
  );

  // Technology image getter with LRU cache
  const getTechnologyImage = useCallback(
    (key: string, isHighlighted: boolean = false): string => {
      const cacheKey = `${key}-${isHighlighted}`;
      const cache = technologyImageCacheRef.current;
      const cached = cache.get(cacheKey);

      if (cached !== undefined) {
        return cached;
      }

      const image = createTechnologyIconDataURL(currentTheme, isHighlighted);
      cache.set(cacheKey, image);
      return image;
    },
    [currentTheme]
  );

  // Tag image getter with LRU cache
  const getTagImage = useCallback(
    (key: string): string => {
      const cacheKey = `${key}`;
      const cache = tagImageCacheRef.current;
      const cached = cache.get(cacheKey);

      if (cached !== undefined) {
        return cached;
      }

      const image = createTagIconDataURL(currentTheme);
      cache.set(cacheKey, image);
      return image;
    },
    [currentTheme]
  );

  // Webpage image getter with LRU cache
  const getWebpageImage = useCallback(
    (key: string, isHighlighted: boolean = false): string => {
      const cacheKey = `${key}-${isHighlighted}`;
      const cache = webpageImageCacheRef.current;
      const cached = cache.get(cacheKey);

      if (cached !== undefined) {
        return cached;
      }

      const image = createWebpageIconDataURL(currentTheme, isHighlighted);
      cache.set(cacheKey, image);
      return image;
    },
    [currentTheme]
  );

  // Memoize the returned object to prevent recreating it on every render
  // This is critical to prevent infinite re-render loops in components that depend on imageCache
  return useMemo(
    () => ({
      getNodeImage,
      getAttributeImage,
      getSeedImage,
      getRiskImage,
      getTechnologyImage,
      getTagImage,
      getWebpageImage,
    }),
    [
      getNodeImage,
      getAttributeImage,
      getSeedImage,
      getRiskImage,
      getTechnologyImage,
      getTagImage,
      getWebpageImage,
    ]
  );
};
